<template>
  <section class="workspace-stack admin-swap-shell">
    <article class="glass-surface workspace-panel admin-swap-hero">
      <div class="workspace-panel__header admin-swap-hero__header">
        <div>
          <span class="workspace-kicker">FOMO Swap</span>
          <h2 class="workspace-title">Marketplace externo Ecuador</h2>
          <p class="workspace-copy">Crea eventos swap en minutos, revisa el mercado inicial y deja la base lista para sellers, delivery, ledger y payouts.</p>
        </div>
        <div class="admin-swap-hero-actions">
          <button class="workspace-button" type="button" :disabled="releasingPayouts" @click="releaseEligiblePayouts">
            <i class="fas fa-vault"></i>
            <span>{{ releasingPayouts ? 'Evaluando...' : 'Evaluar payouts' }}</span>
          </button>
          <button class="workspace-button" type="button" :disabled="reservingPayouts" @click="reservePayoutOrders">
            <i class="fas fa-file-invoice-dollar"></i>
            <span>{{ reservingPayouts ? 'Reservando...' : 'Reservar payouts' }}</span>
          </button>
          <button class="workspace-button" type="button" :disabled="syncingPayouts" @click="syncPayoutOrders">
            <i class="fas fa-rotate"></i>
            <span>{{ syncingPayouts ? 'Sync...' : 'Sync payouts' }}</span>
          </button>
          <button class="workspace-button workspace-button--primary" type="button" @click="focusCreate">
            <i class="fas fa-bolt"></i>
            <span>Crear evento swap</span>
          </button>
        </div>
      </div>

      <div class="admin-swap-metrics">
        <article class="admin-kpi admin-swap-kpi">
          <small>Mercado</small>
          <strong>{{ marketLabel }}</strong>
          <p>Inicio controlado en Ecuador con USD.</p>
        </article>
        <article class="admin-kpi admin-swap-kpi">
          <small>Fee default</small>
          <strong>{{ feeLabel }}</strong>
          <p>Editable por evento/listing; snapshot por venta.</p>
        </article>
        <article class="admin-kpi admin-swap-kpi">
          <small>Release payout</small>
          <strong>{{ releaseLabel }}</strong>
          <p>Después del evento si no hay reclamo.</p>
        </article>
        <article class="admin-kpi admin-swap-kpi">
          <small>Sellers review</small>
          <strong>{{ pendingSellerCount }}</strong>
          <p>Perfiles esperando aprobación.</p>
        </article>
        <article class="admin-kpi admin-swap-kpi">
          <small>Listings review</small>
          <strong>{{ pendingListingCount }}</strong>
          <p>Supply esperando validación.</p>
        </article>
        <article class="admin-kpi admin-swap-kpi">
          <small>Pagos por validar</small>
          <strong>{{ pendingPaymentCount }}</strong>
          <p>Orders esperando confirmación FOMO.</p>
        </article>
        <article class="admin-kpi admin-swap-kpi">
          <small>Reclamos abiertos</small>
          <strong>{{ pendingDisputeCount }}</strong>
          <p>Claims bloqueando payout o refund.</p>
        </article>
      </div>
    </article>

    <div v-if="feedback" :class="['workspace-feedback', feedbackType === 'error' ? 'workspace-feedback--error' : 'workspace-feedback--success']">{{ feedback }}</div>

    <div class="admin-swap-grid">
      <article ref="createPanel" class="glass-surface workspace-panel admin-swap-create">
        <div class="workspace-panel__header">
          <div>
            <span class="workspace-kicker">Crear evento</span>
            <h3 class="workspace-title">Easy peasy, pero bien operable</h3>
            <p class="workspace-copy">Este evento queda como external + swap. El host financiero visible no será el seller.</p>
          </div>
        </div>

        <div class="workspace-form workspace-form--two admin-swap-form">
          <label class="workspace-field workspace-field--wide">
            <span>Nombre</span>
            <input v-model.trim="form.name" class="workspace-input" type="text" placeholder="Bad Bunny Quito Swap" />
          </label>
          <label class="workspace-field workspace-field--wide">
            <span>Descripción</span>
            <textarea v-model.trim="form.description" class="workspace-input admin-swap-textarea" rows="4" placeholder="Marketplace oficial FOMO para tickets externos verificados por operación." />
          </label>
          <label class="workspace-field">
            <span>Categoría</span>
            <select v-model="form.category" class="workspace-input workspace-select">
              <option value="">Selecciona categoría</option>
              <option v-for="category in categories" :key="category.id" :value="category.id">{{ category.name }}</option>
            </select>
          </label>
          <label class="workspace-field">
            <span>País</span>
            <select v-model="form.country" class="workspace-input workspace-select" @change="loadCities">
              <option value="">Ecuador</option>
              <option v-for="country in ecuadorCountries" :key="country.id" :value="country.id">{{ country.name }}</option>
            </select>
          </label>
          <label class="workspace-field">
            <span>Ciudad</span>
            <select v-model="form.city" class="workspace-input workspace-select" @change="loadVenues">
              <option value="">Selecciona ciudad</option>
              <option v-for="city in cities" :key="city.id" :value="city.id">{{ city.name }}</option>
            </select>
          </label>
          <label class="workspace-field">
            <span>Venue</span>
            <select v-model="form.venue" class="workspace-input workspace-select">
              <option value="">Venue pendiente</option>
              <option v-for="venue in venues" :key="venue.id" :value="venue.id">{{ venue.name }}</option>
            </select>
          </label>
          <label class="workspace-field">
            <span>Inicio</span>
            <input v-model="form.start_date" class="workspace-input" type="datetime-local" />
          </label>
          <label class="workspace-field">
            <span>Fin</span>
            <input v-model="form.end_date" class="workspace-input" type="datetime-local" />
          </label>
          <label class="workspace-field">
            <span>Fee default</span>
            <input v-model="form.default_fee_percent" class="workspace-input" type="number" min="0" step="0.0001" />
          </label>
          <label class="workspace-field">
            <span>Release payout</span>
            <input v-model="form.release_after_event_hours" class="workspace-input" type="number" min="1" step="1" />
          </label>
        </div>

        <div class="admin-swap-policy-strip">
          <span>Buyer paga fee por default</span>
          <span>Delivery coordinada</span>
          <span>Review seller/listing requerido</span>
          <span>Email notifications iniciales</span>
        </div>

        <div class="workspace-actions workspace-actions--end">
          <button class="workspace-button workspace-button--ghost" type="button" :disabled="loading" @click="resetForm">Limpiar</button>
          <button class="workspace-button workspace-button--primary" type="button" :disabled="saving || !canCreate" @click="createSwapEvent">{{ saving ? 'Creando...' : 'Crear evento swap' }}</button>
        </div>
      </article>

      <article class="glass-surface workspace-panel admin-swap-list">
        <div class="workspace-panel__header">
          <div>
            <span class="workspace-kicker">Eventos swap</span>
            <h3 class="workspace-title">Directorio operativo</h3>
            <p class="workspace-copy">Primera base; siguientes fases conectan listings, orders, delivery, ledger y payouts.</p>
          </div>
          <button class="workspace-button workspace-button--small" type="button" :disabled="loading" @click="loadData">Actualizar</button>
        </div>

        <div v-if="loading" class="workspace-empty">Cargando FOMO Swap...</div>
        <div v-else-if="!swapEvents.length" class="workspace-empty">Aún no hay eventos swap. Crea el primero para Ecuador.</div>
        <div v-else class="admin-swap-event-list">
          <article v-for="event in swapEvents" :key="event.id" class="admin-swap-event-card">
            <div class="workspace-badge-row workspace-badge-row--compact">
              <span class="workspace-badge">{{ event.country_code || 'EC' }} / {{ event.presentment_currency || 'USD' }}</span>
              <span class="workspace-status-pill is-active">{{ event.commerce_mode }}</span>
              <span class="workspace-badge">{{ event.swap_config && event.swap_config.default_fee_bearer === 'buyer' ? 'buyer fee' : 'seller fee' }}</span>
            </div>
            <h4>{{ event.name }}</h4>
            <p>{{ event.description || 'Sin descripción.' }}</p>
            <div class="admin-swap-card-meta">
              <span><i class="fas fa-calendar"></i>{{ formatDate(event.start_date) }}</span>
              <span><i class="fas fa-location-dot"></i>{{ [event.city_name, event.venue_name].filter(Boolean).join(' · ') || 'Lugar pendiente' }}</span>
              <span><i class="fas fa-percent"></i>{{ percent(event.swap_config && event.swap_config.default_fee_percent) }}</span>
            </div>
            <div class="admin-swap-official-categories">
              <div class="admin-swap-official-head">
                <div>
                  <small>Categorías oficiales</small>
                  <strong>{{ officialTicketOptions(event).length }} configuradas</strong>
                </div>
                <button class="workspace-button workspace-button--small" type="button" :disabled="loadingOfficialTicketKey === `tickets-${event.id}`" @click="loadOfficialTicketsForEvent(event)">Actualizar</button>
              </div>
              <div v-if="loadingOfficialTicketKey === `tickets-${event.id}`" class="workspace-empty workspace-empty--compact">Cargando categorías...</div>
              <div v-else-if="!officialTicketOptions(event).length" class="workspace-empty workspace-empty--compact">Agrega al menos una categoría oficial para habilitar precio sugerido al seller.</div>
              <div v-else class="admin-swap-official-list">
                <article v-for="option in officialTicketOptions(event)" :key="option.id" class="admin-swap-official-pill">
                  <span>{{ option.is_package ? 'Paquete' : 'Entrada' }}</span>
                  <strong>{{ option.name }}</strong>
                  <em>Oficial {{ money(option.official_price, option.currency) }} · Sugerido {{ money(option.recommended_price, option.currency) }}</em>
                </article>
              </div>
              <div class="admin-swap-official-form">
                <label class="workspace-field">
                  <span>Nombre categoría</span>
                  <input class="workspace-input" type="text" :value="officialTicketForm(event).name" placeholder="VIP Central, Mesa 10..." @input="setOfficialTicketField(event, 'name', $event.target.value)" />
                </label>
                <label class="workspace-field">
                  <span>Precio oficial USD</span>
                  <input class="workspace-input" type="number" min="0.01" step="0.01" :value="officialTicketForm(event).price" placeholder="120.00" @input="setOfficialTicketField(event, 'price', $event.target.value)" />
                </label>
                <label class="workspace-field">
                  <span>Cantidad paquete</span>
                  <input class="workspace-input" type="number" min="1" step="1" :value="officialTicketForm(event).quantity" @input="setOfficialTicketField(event, 'quantity', $event.target.value)" />
                </label>
                <label class="workspace-field admin-swap-official-description">
                  <span>Descripción opcional</span>
                  <input class="workspace-input" type="text" :value="officialTicketForm(event).description" placeholder="Contexto visible para operación" @input="setOfficialTicketField(event, 'description', $event.target.value)" />
                </label>
                <button class="workspace-button workspace-button--primary workspace-button--small" type="button" :disabled="savingOfficialTicketKey === `tickets-${event.id}` || !canCreateOfficialTicket(event)" @click="createOfficialTicket(event)">{{ savingOfficialTicketKey === `tickets-${event.id}` ? 'Guardando...' : 'Agregar categoría' }}</button>
              </div>
            </div>
            <div class="workspace-actions workspace-actions--compact workspace-actions--card">
              <button class="workspace-button workspace-button--primary workspace-button--small" type="button" @click="openEvent(event)">Abrir workspace</button>
              <button class="workspace-button workspace-button--small" type="button" @click="openCheckout(event)">Ver checkout</button>
            </div>
          </article>
        </div>
      </article>
    </div>

    <div class="admin-swap-review-grid">
      <article class="glass-surface workspace-panel admin-swap-review-panel">
        <div class="workspace-panel__header">
          <div>
            <span class="workspace-kicker">Sellers</span>
            <h3 class="workspace-title">Aprobación supply-side</h3>
            <p class="workspace-copy">El seller puede crear listings, pero no queda habilitado para aprobación final hasta que FOMO lo active.</p>
          </div>
          <button class="workspace-button workspace-button--small" type="button" :disabled="loading" @click="loadData">Actualizar</button>
        </div>
        <div v-if="loading" class="workspace-empty">Cargando sellers...</div>
        <div v-else-if="!sellers.length" class="workspace-empty">Aún no hay sellers swap.</div>
        <div v-else class="admin-swap-review-list">
          <article v-for="seller in sellers" :key="seller.id" class="admin-swap-review-card">
            <div class="workspace-badge-row workspace-badge-row--compact">
              <span :class="['workspace-status-pill', statusClass(seller.status)]">{{ seller.status }}</span>
              <span class="workspace-badge">{{ seller.country_code }} / {{ seller.default_currency }}</span>
            </div>
            <h4>{{ seller.public_name || seller.legal_name || seller.username || 'Seller swap' }}</h4>
            <p>{{ seller.user_email || 'Sin email' }}</p>
            <div class="admin-swap-card-meta">
              <span><i class="fas fa-building"></i>{{ seller.legal_name || 'Legal name pendiente' }}</span>
              <span><i class="fas fa-check-circle"></i>{{ seller.terms_accepted_at ? 'Términos aceptados' : 'Términos pendientes' }}</span>
              <span v-if="seller.blocked_reason"><i class="fas fa-triangle-exclamation"></i>{{ seller.blocked_reason }}</span>
            </div>
            <div class="workspace-actions workspace-actions--compact workspace-actions--card">
              <button class="workspace-button workspace-button--primary workspace-button--small" type="button" :disabled="reviewingKey === `seller-${seller.id}` || seller.status === 'active'" @click="reviewSeller(seller, 'approve')">Aprobar</button>
              <button class="workspace-button workspace-button--small" type="button" :disabled="reviewingKey === `seller-${seller.id}`" @click="reviewSeller(seller, 'review')">Revisar</button>
              <button class="workspace-button workspace-button--small workspace-button--danger" type="button" :disabled="reviewingKey === `seller-${seller.id}`" @click="reviewSeller(seller, 'block')">Bloquear</button>
            </div>
          </article>
        </div>
      </article>

      <article class="glass-surface workspace-panel admin-swap-review-panel">
        <div class="workspace-panel__header">
          <div>
            <span class="workspace-kicker">Listings</span>
            <h3 class="workspace-title">Tickets externos por validar</h3>
            <p class="workspace-copy">Aprobación por listing; los units quedan modelados para holds, checkout y delivery posterior.</p>
          </div>
        </div>
        <div v-if="loading" class="workspace-empty">Cargando entradas...</div>
        <div v-else-if="!listings.length" class="workspace-empty">Aún no hay entradas swap.</div>
        <div v-else class="admin-swap-review-list">
          <article v-for="listing in listings" :key="listing.id" class="admin-swap-review-card">
            <div class="workspace-badge-row workspace-badge-row--compact">
              <span :class="['workspace-status-pill', listingStatusClass(listing.status)]">{{ listing.status }}</span>
              <span class="workspace-badge">{{ listing.quantity }} x {{ money(listing.unit_price, listing.currency) }}</span>
              <span class="workspace-badge">{{ listing.delivery_method }}</span>
            </div>
            <h4>{{ listing.title || listing.event_name || 'Listing swap' }}</h4>
            <p>{{ listing.event_name }} · {{ listing.seller_public_name || listing.seller_user_email || 'Seller' }}</p>
            <div class="admin-swap-card-meta">
              <span><i class="fas fa-location-dot"></i>{{ [listing.zone_label, listing.section_label, listing.row_label].filter(Boolean).join(' · ') || 'Zona pendiente' }}</span>
              <span><i class="fas fa-chair"></i>{{ seatSummary(listing) }}</span>
              <span><i class="fas fa-calendar"></i>{{ formatDate(listing.event_start_date) }}</span>
            </div>
            <div class="admin-swap-proof-strip">
              <div>
                <small>Evidencia seller</small>
                <strong>{{ proofStatusLabel(listing) }}</strong>
                <span>{{ proofHelper(listing) }}</span>
              </div>
              <a v-if="listing.proof_file_url" :href="listing.proof_file_url" target="_blank" rel="noopener">Abrir proof</a>
            </div>
            <div class="workspace-actions workspace-actions--compact workspace-actions--card">
              <button class="workspace-button workspace-button--primary workspace-button--small" type="button" :disabled="reviewingKey === `listing-${listing.id}` || listing.status === 'approved'" @click="reviewListing(listing, 'approve')">Aprobar</button>
              <button class="workspace-button workspace-button--small" type="button" :disabled="reviewingKey === `listing-${listing.id}`" @click="reviewListing(listing, 'review')">Pedir revisión</button>
              <button class="workspace-button workspace-button--small workspace-button--danger" type="button" :disabled="reviewingKey === `listing-${listing.id}`" @click="reviewListing(listing, 'reject')">Rechazar</button>
            </div>
          </article>
        </div>
      </article>
    </div>

    <article class="glass-surface workspace-panel admin-swap-orders-panel">
      <div class="workspace-panel__header">
        <div>
          <span class="workspace-kicker">Compras</span>
          <h3 class="workspace-title">Pagos y entregas por revisar</h3>
          <p class="workspace-copy">Primero se muestran los reclamos, las entregas vencidas y los pagos que llevan más de cinco minutos pendientes.</p>
        </div>
        <FomoButton variant="secondary" icon="" type="button" :disabled="loading" @click="loadData">Actualizar</FomoButton>
      </div>

      <div class="swap-ops-filters"><label>Buscar compra<input v-model.trim="orderSearch" placeholder="Número de compra o evento" /></label><label>Mostrar<select v-model="orderQueue"><option value="attention">Necesitan atención</option><option value="pending">Pagos pendientes</option><option value="delivery">Entregas vencidas</option><option value="all">Todas las compras</option></select></label><span>{{ filteredOrders.length }} compras</span></div>
      <div v-if="loading" class="workspace-empty">Cargando compras...</div>
      <div v-else-if="!orders.length" class="workspace-empty">Aún no hay compras de Swap.</div>
      <div v-else-if="!filteredOrders.length" class="workspace-empty">No hay compras para este filtro.</div>
      <div v-else class="admin-swap-order-list">
        <article v-for="order in filteredOrders" :key="order.id" class="admin-swap-order-card fomo-dashboard-card">
          <div class="admin-swap-order-head">
            <div>
              <div class="workspace-badge-row workspace-badge-row--compact">
                <span :class="['workspace-status-pill', orderStatusClass(order.status)]">{{ opsLabel(order.status) }}</span>
                <span :class="['workspace-status-pill', paymentStatusClass(order.payment_status)]">Pago {{ opsLabel(order.payment_status) }}</span>
                <span class="workspace-badge">Cobro {{ opsLabel(order.payout_status) }}</span>
                <span v-if="order.is_multi_seller" class="workspace-badge">Varios vendedores</span>
              </div>
              <h4>{{ order.order_number }}</h4>
              <p>{{ order.event_name || 'Evento swap' }} · {{ formatDate(order.created_at) }}</p>
            </div>
            <strong class="admin-swap-order-total">{{ orderTotalLabel(order) }}</strong>
          </div>

          <div class="admin-swap-order-meta">
            <span><i class="fas fa-ticket"></i>{{ orderItemCount(order) }} entradas</span>
            <span><i class="fas fa-users"></i>{{ orderSellerCount(order) }} vendedores</span>
            <span><i class="fas fa-credit-card"></i>{{ verificationRail(order) }}</span>
            <span><i class="fas fa-shield-halved"></i>{{ verificationStatus(order) }}</span>
          </div>

          <div class="admin-swap-finance-strip">
            <span>Entrega: {{ deliveryStatusSummary(order) }}</span>
            <span>Saldo: {{ allocationStatusSummary(order) }}</span>
          </div>

          <div v-if="Array.isArray(order.deliveries) && order.deliveries.length" class="admin-swap-delivery-review">
            <article v-for="delivery in order.deliveries" :key="delivery.id" class="admin-swap-delivery-row fomo-card">
              <div>
                <strong>Entrega #{{ delivery.id }}</strong>
                <span>{{ opsLabel(delivery.status) }} · {{ opsLabel(delivery.delivery_method) }}</span>
                <div v-if="Array.isArray(delivery.artifacts) && delivery.artifacts.length" class="admin-swap-artifact-list">
                  <article v-for="artifact in delivery.artifacts" :key="artifact.id">
                    <small>{{ artifact.visible_to_buyer ? 'Buyer' : 'Privado' }} · {{ artifact.source }} · {{ artifact.artifact_type }}</small>
                    <strong>{{ artifact.title || 'Artifact delivery' }}</strong>
                    <span v-if="artifact.external_reference">{{ artifact.external_reference }}</span>
                    <span v-if="artifact.instructions">{{ artifact.instructions }}</span>
                    <a v-if="artifact.file_url" :href="artifact.file_url" target="_blank" rel="noopener">{{ artifact.original_name || 'Archivo' }}</a>
                  </article>
                </div>
              </div>
              <div class="workspace-actions workspace-actions--compact">
                <FomoButton variant="secondary" icon="" type="button" :disabled="!canUpdateDelivery(order, delivery, 'delivery_in_progress')" @click="updateDeliveryStatus(order, delivery, 'delivery_in_progress')">En progreso</FomoButton>
                <FomoButton variant="action" icon="" type="button" :disabled="!canUpdateDelivery(order, delivery, 'buyer_accessible')" @click="updateDeliveryStatus(order, delivery, 'buyer_accessible')">Disponible para el comprador</FomoButton>
              </div>
              <div v-if="canUpdateDelivery(order, delivery, 'buyer_accessible')" class="admin-swap-delivery-artifact-form">
                <label class="workspace-field">
                  <span>Título de la entrega</span>
                  <input class="workspace-input" type="text" :value="deliveryArtifactField(delivery, 'artifact_title')" placeholder="Ticket validado / transferencia lista" @input="setDeliveryArtifactField(delivery, 'artifact_title', $event.target.value)" />
                </label>
                <label class="workspace-field">
                  <span>Referencia para el comprador</span>
                  <input class="workspace-input" type="text" :value="deliveryArtifactField(delivery, 'external_reference')" placeholder="Folio, código externo o referencia" @input="setDeliveryArtifactField(delivery, 'external_reference', $event.target.value)" />
                </label>
                <label class="workspace-field">
                  <span>Link externo</span>
                  <input class="workspace-input" type="url" :value="deliveryArtifactField(delivery, 'external_url')" placeholder="https://..." @input="setDeliveryArtifactField(delivery, 'external_url', $event.target.value)" />
                </label>
                <label class="workspace-field">
                  <span>Archivo para el comprador</span>
                  <input class="workspace-input" type="file" @change="setDeliveryArtifactFile(delivery, $event)" />
                </label>
                <label class="workspace-field admin-swap-artifact-note">
                  <span>Instrucciones de entrega</span>
                  <textarea class="workspace-input" rows="2" :value="deliveryArtifactField(delivery, 'instructions')" placeholder="Instrucciones visibles al buyer. FOMO coordina, Swap no genera QR nuevo." @input="setDeliveryArtifactField(delivery, 'instructions', $event.target.value)"></textarea>
                </label>
              </div>
            </article>
          </div>

          <div class="admin-swap-payment-form">
            <label class="workspace-field">
              <span>Referencia del proveedor</span>
              <input class="workspace-input" type="text" :value="verificationField(order, 'provider_reference')" placeholder="Stripe PI, PayPal capture, transferencia..." @input="setVerificationField(order, 'provider_reference', $event.target.value)" />
            </label>
            <label class="workspace-field admin-swap-note-field">
              <span>Nota interna</span>
              <input class="workspace-input" type="text" :value="verificationField(order, 'note')" placeholder="Motivo, evidencia o comentario interno" @input="setVerificationField(order, 'note', $event.target.value)" />
            </label>
          </div>

          <div class="workspace-actions workspace-actions--compact workspace-actions--card">
            <FomoButton variant="action" icon="" type="button" :disabled="verifyingKey === `order-${order.order_number}` || order.payment_status === 'verified'" @click="verifyOrderPayment(order, 'admin_confirmed')">Confirmar pago</FomoButton>
            <FomoButton variant="secondary" icon="" type="button" :disabled="verifyingKey === `order-${order.order_number}`" @click="verifyOrderPayment(order, 'mismatch')">Marcar diferencia</FomoButton>
            <FomoButton variant="secondary" icon="" type="button" :disabled="verifyingKey === `order-${order.order_number}`" @click="verifyOrderPayment(order, 'failed')">Pago fallido</FomoButton>
          </div>

          <details class="admin-swap-refund-details fomo-card"><summary>Registrar un reembolso manual</summary><div class="admin-swap-manual-refund-form">
            <div class="admin-swap-refund-heading">
              <div>
                <small>Reembolso manual</small>
                <strong>PayPal/Rebill/Nuvei/manual</strong>
              </div>
              <span>{{ canManualRefund(order) ? 'Listo para registrar' : 'Requiere pago confirmado y comprobante.' }}</span>
            </div>
            <label class="workspace-field">
              <span>Monto a reembolsar (USD)</span>
              <input class="workspace-input" type="number" min="0.01" step="0.01" :value="manualRefundField(order, 'amount')" @input="setManualRefundField(order, 'amount', $event.target.value)" />
            </label>
            <label class="workspace-field">
              <span>Proveedor</span>
              <select class="workspace-input workspace-select" :value="manualRefundField(order, 'provider')" @change="setManualRefundField(order, 'provider', $event.target.value)">
                <option v-for="provider in manualRefundProviderOptions" :key="provider.value" :value="provider.value">{{ provider.label }}</option>
              </select>
            </label>
            <label class="workspace-field">
              <span>Referencia del reembolso</span>
              <input class="workspace-input" type="text" :value="manualRefundField(order, 'provider_reference')" placeholder="ID refund/capture/provider" @input="setManualRefundField(order, 'provider_reference', $event.target.value)" />
            </label>
            <label class="workspace-field">
              <span>Saldos afectados (opcional)</span>
              <input class="workspace-input" type="text" :value="manualRefundField(order, 'allocation_ids_text')" placeholder="12, 13 o vacío = prorrata completa" @input="setManualRefundField(order, 'allocation_ids_text', $event.target.value)" />
            </label>
            <label class="workspace-field">
              <span>Comprobante privado</span>
              <input class="workspace-input" type="file" @change="setManualRefundFile(order, $event)" />
            </label>
            <label class="workspace-field admin-swap-note-field">
              <span>Nota del reembolso</span>
              <input class="workspace-input" type="text" :value="manualRefundField(order, 'note')" placeholder="Contexto ops, proveedor, conciliación" @input="setManualRefundField(order, 'note', $event.target.value)" />
            </label>
            <FomoButton variant="secondary" icon="" type="button" :disabled="!canManualRefund(order)" @click="submitManualRefund(order)">{{ manualRefundingKey === `refund-${order.order_number}` ? 'Registrando...' : 'Registrar reembolso manual' }}</FomoButton>
          </div></details>
        </article>
      </div>
    </article>

    <article class="glass-surface workspace-panel admin-swap-disputes-panel">
      <div class="workspace-panel__header">
        <div>
          <span class="workspace-kicker">Disputes</span>
          <h3 class="workspace-title">Reclamos, refunds y resolución seller</h3>
          <p class="workspace-copy">Cada claim bloquea la allocation hasta que FOMO pida evidencia, apruebe refund o resuelva a favor del seller.</p>
        </div>
        <button class="workspace-button workspace-button--small" type="button" :disabled="loading" @click="loadData">Actualizar</button>
      </div>

      <div v-if="loading" class="workspace-empty">Cargando reclamos swap...</div>
      <div v-else-if="!disputes.length" class="workspace-empty">No hay reclamos swap abiertos o históricos.</div>
      <div v-else class="admin-swap-dispute-list">
        <article v-for="dispute in disputes" :key="dispute.id" class="admin-swap-dispute-card">
          <div class="admin-swap-order-head">
            <div>
              <div class="workspace-badge-row workspace-badge-row--compact">
                <span :class="['workspace-status-pill', disputeStatusClass(dispute.status)]">{{ dispute.status }}</span>
                <span class="workspace-badge">{{ dispute.reason || 'sin motivo' }}</span>
                <span class="workspace-badge">seller {{ dispute.seller_id || 'n/a' }}</span>
              </div>
              <h4>{{ dispute.order_number }}</h4>
              <p>{{ dispute.event_name || 'Evento swap' }} · {{ formatDate(dispute.opened_at) }}</p>
            </div>
          </div>

          <div class="admin-swap-card-meta">
            <span><i class="fas fa-ticket"></i>Order item {{ dispute.order_item || 'n/a' }}</span>
            <span><i class="fas fa-scale-balanced"></i>Allocation {{ dispute.allocation || 'n/a' }}</span>
            <span v-if="dispute.resolved_at"><i class="fas fa-check"></i>Resuelto {{ formatDate(dispute.resolved_at) }}</span>
          </div>
          <p>{{ dispute.description || 'Sin detalle del buyer.' }}</p>

          <label class="workspace-field admin-swap-note-field">
            <span>Resolución ops</span>
            <input class="workspace-input" type="text" :value="disputeReviewField(dispute, 'resolution')" placeholder="Evidencia solicitada, refund aprobado, seller validado..." @input="setDisputeReviewField(dispute, 'resolution', $event.target.value)" />
          </label>

          <div class="workspace-actions workspace-actions--compact workspace-actions--card">
            <button class="workspace-button workspace-button--small" type="button" :disabled="reviewingDisputeKey === `dispute-${dispute.id}` || isResolvedDispute(dispute)" @click="reviewDispute(dispute, 'request_evidence')">Pedir evidencia</button>
            <button class="workspace-button workspace-button--small workspace-button--danger" type="button" :disabled="reviewingDisputeKey === `dispute-${dispute.id}` || isResolvedDispute(dispute)" @click="reviewDispute(dispute, 'resolve_buyer_refund')">Refund buyer</button>
            <button class="workspace-button workspace-button--primary workspace-button--small" type="button" :disabled="reviewingDisputeKey === `dispute-${dispute.id}` || isResolvedDispute(dispute)" @click="reviewDispute(dispute, 'resolve_seller')">Resolver seller</button>
          </div>
        </article>
      </div>
    </article>

    <article class="glass-surface workspace-panel admin-swap-messages-panel">
      <div class="workspace-panel__header">
        <div>
          <span class="workspace-kicker">Mensajes</span>
          <h3 class="workspace-title">FOMO coordina entregas</h3>
          <p class="workspace-copy">Threads internos por orden swap. No se comparte contacto directo buyer-seller; FOMO centraliza evidencia, entrega y reclamos.</p>
        </div>
        <button class="workspace-button workspace-button--small" type="button" :disabled="loading" @click="loadData">Actualizar</button>
      </div>

      <div v-if="loading && !messageThreads.length" class="workspace-empty">Cargando mensajes swap...</div>
      <div v-else-if="!messageThreads.length" class="workspace-empty">Aún no hay threads de mensajes Swap.</div>
      <div v-else class="admin-swap-message-list">
        <article v-for="thread in messageThreads" :key="thread.id" class="admin-swap-message-card">
          <div class="workspace-badge-row workspace-badge-row--compact">
            <span class="workspace-status-pill is-active">{{ thread.status }}</span>
            <span class="workspace-badge">{{ thread.order_number }}</span>
            <span class="workspace-badge">seller {{ thread.seller || 'ops' }}</span>
          </div>
          <h4>{{ thread.event_name }}</h4>
          <p>{{ thread.seller_public_name || 'Thread operativo FOMO' }}</p>
          <div class="admin-swap-thread-messages">
            <p v-for="message in thread.messages" :key="message.id" :class="['admin-swap-thread-message', `is-${message.sender_role}`]">
              <small>{{ message.sender_role === 'system' ? 'FOMO sistema' : message.sender_role }}</small>
              <span>{{ message.body }}</span>
            </p>
          </div>
          <div class="admin-swap-message-compose">
            <textarea class="workspace-input" :value="messageDraft(thread.id)" rows="2" placeholder="Responder como FOMO" @input="setMessageDraft(thread.id, $event.target.value)"></textarea>
            <button class="workspace-button workspace-button--primary workspace-button--small" type="button" :disabled="sendingMessageKey === `thread-${thread.id}` || !messageDraft(thread.id)" @click="sendAdminMessage(thread)">{{ sendingMessageKey === `thread-${thread.id}` ? 'Enviando...' : 'Enviar update' }}</button>
          </div>
        </article>
      </div>
    </article>
  </section>
</template>

<script>
import { apiService, apiServiceForm } from '../../common/api.services.js'
import '@/styles/fomo-dashboard.css'
import FomoButton from '@/components/ui/FomoButton.vue'

export default {
  name: 'AdminSwapOpsView',
  components: { FomoButton },
  data() {
    return {
      loading: false,
      orderQueue: 'attention',
      orderSearch: '',
      saving: false,
      releasingPayouts: false,
      reservingPayouts: false,
      syncingPayouts: false,
      manualRefundingKey: '',
      loadingOfficialTicketKey: '',
      savingOfficialTicketKey: '',
      feedback: '',
      feedbackType: 'success',
      dashboard: null,
      swapEvents: [],
      sellers: [],
      listings: [],
      orders: [],
      disputes: [],
      messageThreads: [],
      categories: [],
      countries: [],
      cities: [],
      venues: [],
      reviewingKey: '',
      reviewingDisputeKey: '',
      verifyingKey: '',
      updatingDeliveryKey: '',
      sendingMessageKey: '',
      orderVerificationForms: {},
      manualRefundForms: {},
      deliveryArtifactForms: {},
      disputeReviewForms: {},
      messageDrafts: {},
      officialTicketPayloads: {},
      officialTicketForms: {},
      form: this.defaultForm(),
      manualRefundProviderOptions: [
        { value: 'paypal', label: 'PayPal' },
        { value: 'rebill', label: 'Rebill' },
        { value: 'mercadopago', label: 'Mercado Pago' },
        { value: 'nuvei', label: 'Nuvei' },
        { value: 'bank_transfer', label: 'Bank transfer' },
        { value: 'manual_external', label: 'Manual externo' },
        { value: 'other', label: 'Otro' },
      ],
    }
  },
  computed: {
    filteredOrders() {
      const now = Date.now()
      const priority = order => {
        if (order.fomo_review_status === 'needs_review' || order.payout_status === 'blocked') return 3
        if (order.payment_status === 'verified' && (order.deliveries || []).some(d => ['pending_seller_action', 'seller_confirmed', 'delivery_in_progress'].includes(d.status) && d.due_at && new Date(d.due_at).getTime() < now)) return 2
        if (['pending', 'unverified'].includes(order.payment_status) && now - new Date(order.created_at).getTime() > 300000) return 1
        return 0
      }
      const search = this.orderSearch.toLowerCase()
      return this.orders.filter(order => `${order.order_number} ${order.event_name}`.toLowerCase().includes(search))
        .filter(order => this.orderQueue === 'all' || (this.orderQueue === 'attention' && priority(order) > 0) || (this.orderQueue === 'pending' && ['pending', 'unverified'].includes(order.payment_status)) || (this.orderQueue === 'delivery' && priority(order) === 2))
        .sort((a, b) => priority(b) - priority(a) || new Date(a.created_at) - new Date(b.created_at))
    },
    ecuadorCountries() {
      return this.countries.filter((country) => String(country.code || '').toUpperCase() === 'EC')
    },
    marketLabel() {
      const market = this.dashboard && this.dashboard.market
      return market ? `${market.country_code} / ${market.currency}` : 'EC / USD'
    },
    feeLabel() {
      const market = this.dashboard && this.dashboard.market
      return this.percent(market && market.default_fee_percent)
    },
    releaseLabel() {
      const market = this.dashboard && this.dashboard.market
      return `${(market && market.release_after_event_hours) || 24}h post-evento`
    },
    pendingSellerCount() {
      return this.sellers.filter((seller) => ['pending', 'review'].includes(seller.status)).length
    },
    pendingListingCount() {
      return this.listings.filter((listing) => listing.status === 'pending_review').length
    },
    pendingPaymentCount() {
      return this.orders.filter((order) => !['verified', 'failed', 'refunded', 'chargeback'].includes(order.payment_status)).length
    },
    pendingDisputeCount() {
      return this.disputes.filter((dispute) => !this.isResolvedDispute(dispute)).length
    },
    canCreate() {
      return Boolean(this.form.name && this.form.start_date && this.form.end_date)
    },
  },
  created() {
    this.loadData()
    this.loadOptions()
  },
  methods: {
    opsLabel(value) {
      const labels = { unverified: 'por verificar', verified: 'confirmado', failed: 'fallido', pending: 'pendiente', pending_payment: 'pago pendiente', pending_payment_verification: 'Pago por verificar', pending_seller_action: 'Por entregar', delivery_in_progress: 'En preparación', buyer_accessible: 'Disponible para el comprador', delivered: 'Entregada', cancelled: 'Cancelada', refunded: 'Reembolsada', chargeback: 'Con reclamo', physical_handoff: 'Entrega física', app_transfer: 'Transferencia por app', qr_code: 'Archivo de entrada', held: 'retenido', releasable: 'disponible', paid: 'enviado' }
      return labels[value] || String(value || 'pendiente').replaceAll('_', ' ')
    },
    defaultForm() {
      return {
        name: '',
        description: '',
        category: '',
        country: '',
        city: '',
        venue: '',
        start_date: '',
        end_date: '',
        timezone: 'America/Guayaquil',
        default_fee_percent: '0.2000',
        release_after_event_hours: 24,
      }
    },
    defaultOfficialTicketForm() {
      return { name: '', description: '', price: '', quantity: 1 }
    },
    percent(value) {
      const numeric = Number(value || 0)
      return `${(numeric * 100).toFixed(numeric % 0.01 === 0 ? 0 : 2)}%`
    },
    formatDate(value) {
      if (!value) return 'Fecha pendiente'
      try { return new Intl.DateTimeFormat('es-EC', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) } catch (_error) { return value }
    },
    money(value, currency = 'USD') {
      const amount = Number(value || 0)
      try { return new Intl.NumberFormat('es-EC', { style: 'currency', currency: currency || 'USD' }).format(amount) } catch (_error) { return `${amount.toFixed(2)} ${currency || 'USD'}` }
    },
    statusClass(status) {
      if (status === 'active') return 'is-active'
      if (status === 'blocked') return 'is-danger'
      return 'is-warning'
    },
    listingStatusClass(status) {
      if (status === 'approved') return 'is-active'
      if (['rejected', 'blocked', 'cancelled'].includes(status)) return 'is-danger'
      return 'is-warning'
    },
    orderStatusClass(status) {
      if (['confirmed', 'in_delivery', 'delivered', 'completed'].includes(status)) return 'is-active'
      if (['cancelled', 'partially_refunded'].includes(status)) return 'is-danger'
      return 'is-warning'
    },
    paymentStatusClass(status) {
      if (status === 'verified') return 'is-active'
      if (['failed', 'refunded', 'chargeback'].includes(status)) return 'is-danger'
      return 'is-warning'
    },
    disputeStatusClass(status) {
      if (status === 'resolved_seller') return 'is-active'
      if (['refunded', 'resolved_buyer'].includes(status)) return 'is-danger'
      return 'is-warning'
    },
    isResolvedDispute(dispute) {
      return Boolean(dispute && ['resolved_seller', 'resolved_buyer', 'refunded'].includes(dispute.status))
    },
    seatSummary(listing) {
      const unitSeats = Array.isArray(listing.units) ? listing.units.map((unit) => unit.seat_label).filter(Boolean) : []
      if (unitSeats.length) return unitSeats.join(', ')
      return [listing.seat_start, listing.seat_end].filter(Boolean).join(' - ') || (listing.seats_together ? 'Juntos' : 'No juntos')
    },
    proofStatusLabel(listing) {
      const labels = {
        missing: 'Sin evidencia',
        submitted: 'Evidencia enviada',
        pending_review: 'Pendiente FOMO',
        approved: 'Aprobada',
        rejected: 'Rechazada',
      }
      return labels[listing && listing.proof_status] || (listing && listing.proof_status) || 'Sin evidencia'
    },
    proofHelper(listing) {
      const proof = (listing && listing.proof_metadata) || {}
      return proof.note || proof.external_reference || 'El seller debe subir archivo o referencia antes de aprobación final.'
    },
    officialTicketOptions(event) {
      const payload = this.officialTicketPayloads[(event && event.id) || ''] || {}
      return Array.isArray(payload.options) ? payload.options : []
    },
    officialTicketForm(event) {
      return this.officialTicketForms[(event && event.id) || ''] || this.defaultOfficialTicketForm()
    },
    setOfficialTicketField(event, field, value) {
      if (!event || !event.id) return
      this.officialTicketForms = {
        ...this.officialTicketForms,
        [event.id]: {
          ...this.defaultOfficialTicketForm(),
          ...(this.officialTicketForms[event.id] || {}),
          [field]: value,
        },
      }
    },
    canCreateOfficialTicket(event) {
      const form = this.officialTicketForm(event)
      return Boolean(event && event.id && String(form.name || '').trim() && Number(form.price || 0) > 0 && Number(form.quantity || 0) >= 1)
    },
    orderTotals(order) {
      return (order && order.totals) || {}
    },
    orderTotalLabel(order) {
      const totals = this.orderTotals(order)
      return this.money(totals.customer_total || 0, totals.currency || 'USD')
    },
    orderItemCount(order) {
      return Array.isArray(order.items) ? order.items.length : Number(this.orderTotals(order).item_count || 0)
    },
    orderSellerCount(order) {
      if (!Array.isArray(order.items)) return Number(this.orderTotals(order).seller_count || 0)
      return new Set(order.items.map((item) => item.seller).filter(Boolean)).size
    },
    verificationRail(order) {
      const verification = order && order.payment_verification
      return verification && verification.rail ? verification.rail : 'Método pendiente'
    },
    verificationStatus(order) {
      const verification = order && order.payment_verification
      return verification && verification.status ? this.opsLabel(verification.status) : 'sin verificación'
    },
    deliveryStatusSummary(order) {
      return this.statusSummary(order && order.deliveries)
    },
    allocationStatusSummary(order) {
      return this.statusSummary(order && order.finance_allocations)
    },
    statusSummary(items) {
      if (!Array.isArray(items) || !items.length) return 'pendiente'
      const counts = items.reduce((acc, item) => {
        const status = item.status || 'unknown'
        acc[status] = (acc[status] || 0) + 1
        return acc
      }, {})
      return Object.keys(counts).map((status) => `${this.opsLabel(status)}: ${counts[status]}`).join(' · ')
    },
    syncOrderVerificationForms(orders) {
      const forms = {}
      const orderList = orders || []
      orderList.forEach((order) => {
        const key = order.order_number
        const existing = this.orderVerificationForms[key] || {}
        const verification = order.payment_verification || {}
        forms[key] = {
          provider_reference: Object.prototype.hasOwnProperty.call(existing, 'provider_reference') ? existing.provider_reference : (verification.provider_reference || ''),
          note: Object.prototype.hasOwnProperty.call(existing, 'note') ? existing.note : '',
        }
      })
      this.orderVerificationForms = forms
    },
    syncManualRefundForms(orders) {
      const forms = {}
      const orderList = orders || []
      orderList.forEach((order) => {
        const key = order.order_number
        const existing = this.manualRefundForms[key] || {}
        const rail = this.verificationRail(order)
        const provider = this.manualRefundProviderOptions.some((item) => item.value === rail) ? rail : 'paypal'
        forms[key] = {
          amount: Object.prototype.hasOwnProperty.call(existing, 'amount') ? existing.amount : String(this.orderTotals(order).customer_total || ''),
          provider: Object.prototype.hasOwnProperty.call(existing, 'provider') ? existing.provider : provider,
          provider_reference: Object.prototype.hasOwnProperty.call(existing, 'provider_reference') ? existing.provider_reference : '',
          allocation_ids_text: Object.prototype.hasOwnProperty.call(existing, 'allocation_ids_text') ? existing.allocation_ids_text : '',
          note: Object.prototype.hasOwnProperty.call(existing, 'note') ? existing.note : '',
          proof_file: Object.prototype.hasOwnProperty.call(existing, 'proof_file') ? existing.proof_file : null,
        }
      })
      this.manualRefundForms = forms
    },
    syncDeliveryArtifactForms(orders) {
      const forms = {}
      const orderList = orders || []
      orderList.forEach((order) => {
        const deliveries = order.deliveries || []
        deliveries.forEach((delivery) => {
          const existing = this.deliveryArtifactForms[delivery.id] || {}
          forms[delivery.id] = {
            artifact_title: Object.prototype.hasOwnProperty.call(existing, 'artifact_title') ? existing.artifact_title : '',
            external_reference: Object.prototype.hasOwnProperty.call(existing, 'external_reference') ? existing.external_reference : '',
            external_url: Object.prototype.hasOwnProperty.call(existing, 'external_url') ? existing.external_url : '',
            instructions: Object.prototype.hasOwnProperty.call(existing, 'instructions') ? existing.instructions : '',
            artifact_file: Object.prototype.hasOwnProperty.call(existing, 'artifact_file') ? existing.artifact_file : null,
          }
        })
      })
      this.deliveryArtifactForms = forms
    },
    syncDisputeReviewForms(disputes) {
      const forms = {}
      const disputeList = disputes || []
      disputeList.forEach((dispute) => {
        const key = dispute.id
        const existing = this.disputeReviewForms[key] || {}
        forms[key] = {
          resolution: Object.prototype.hasOwnProperty.call(existing, 'resolution') ? existing.resolution : (dispute.resolution || ''),
        }
      })
      this.disputeReviewForms = forms
    },
    verificationField(order, field) {
      const form = this.orderVerificationForms[(order && order.order_number) || ''] || {}
      return Object.prototype.hasOwnProperty.call(form, field) ? form[field] : ''
    },
    setVerificationField(order, field, value) {
      if (!order || !order.order_number) return
      this.orderVerificationForms = {
        ...this.orderVerificationForms,
        [order.order_number]: {
          ...(this.orderVerificationForms[order.order_number] || {}),
          [field]: value,
        },
      }
    },
    manualRefundField(order, field) {
      const form = this.manualRefundForms[(order && order.order_number) || ''] || {}
      return Object.prototype.hasOwnProperty.call(form, field) ? form[field] : ''
    },
    setManualRefundField(order, field, value) {
      if (!order || !order.order_number) return
      this.manualRefundForms = {
        ...this.manualRefundForms,
        [order.order_number]: {
          ...(this.manualRefundForms[order.order_number] || {}),
          [field]: value,
        },
      }
    },
    setManualRefundFile(order, event) {
      const file = event && event.target && event.target.files && event.target.files[0] ? event.target.files[0] : null
      this.setManualRefundField(order, 'proof_file', file)
    },
    manualRefundAllocationIds(order) {
      const text = String(this.manualRefundField(order, 'allocation_ids_text') || '')
      return text.split(/[\s,]+/).map((item) => Number(item)).filter((item) => Number.isInteger(item) && item > 0)
    },
    canManualRefund(order) {
      if (!order || !order.order_number || this.manualRefundingKey) return false
      if (order.payment_status !== 'verified') return false
      const amount = Number(this.manualRefundField(order, 'amount') || 0)
      const hasProof = Boolean(this.manualRefundField(order, 'provider_reference') || this.manualRefundField(order, 'proof_file'))
      return amount > 0 && hasProof
    },
    deliveryArtifactField(delivery, field) {
      const form = this.deliveryArtifactForms[(delivery && delivery.id) || ''] || {}
      return Object.prototype.hasOwnProperty.call(form, field) ? form[field] : ''
    },
    setDeliveryArtifactField(delivery, field, value) {
      if (!delivery || !delivery.id) return
      this.deliveryArtifactForms = {
        ...this.deliveryArtifactForms,
        [delivery.id]: {
          ...(this.deliveryArtifactForms[delivery.id] || {}),
          [field]: value,
        },
      }
    },
    setDeliveryArtifactFile(delivery, event) {
      const file = event && event.target && event.target.files && event.target.files[0] ? event.target.files[0] : null
      this.setDeliveryArtifactField(delivery, 'artifact_file', file)
    },
    disputeReviewField(dispute, field) {
      const form = this.disputeReviewForms[(dispute && dispute.id) || ''] || {}
      return Object.prototype.hasOwnProperty.call(form, field) ? form[field] : ''
    },
    setDisputeReviewField(dispute, field, value) {
      if (!dispute || !dispute.id) return
      this.disputeReviewForms = {
        ...this.disputeReviewForms,
        [dispute.id]: {
          ...(this.disputeReviewForms[dispute.id] || {}),
          [field]: value,
        },
      }
    },
    canUpdateDelivery(order, delivery, status) {
      if (!order || !delivery || this.updatingDeliveryKey) return false
      if (order.payment_status !== 'verified') return false
      if (status === 'delivery_in_progress') return ['pending_seller_action', 'seller_confirmed'].includes(delivery.status)
      if (status === 'buyer_accessible') return ['seller_confirmed', 'delivery_in_progress'].includes(delivery.status)
      return false
    },
    extractError(payload, fallback) {
      if (!payload) return fallback
      if (payload.err) return payload.err
      if (payload.detail) return Array.isArray(payload.detail) ? payload.detail.join(', ') : payload.detail
      const firstValue = typeof payload === 'object' ? Object.values(payload)[0] : null
      if (Array.isArray(firstValue)) return firstValue[0]
      return firstValue || fallback
    },
    toIso(value) {
      if (!value) return ''
      const parsed = new Date(value)
      return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString()
    },
    async loadData(options = {}) {
      this.loading = true
      if (!options.preserveFeedback) this.feedback = ''
      const [dashboard, events, sellers, listings, orders, disputes, messages] = await Promise.all([
        apiService('/api/admin/swap/dashboard/'),
        apiService('/api/admin/swap/events/'),
        apiService('/api/admin/swap/sellers/'),
        apiService('/api/admin/swap/listings/'),
        apiService('/api/admin/swap/orders/'),
        apiService('/api/admin/swap/disputes/'),
        apiService('/api/admin/swap/messages/'),
      ])
      this.dashboard = dashboard && !dashboard.err ? dashboard : null
      this.swapEvents = Array.isArray(events) ? events : []
      await this.loadOfficialTicketsForEvents(this.swapEvents)
      this.sellers = Array.isArray(sellers) ? sellers : []
      this.listings = Array.isArray(listings) ? listings : []
      this.orders = Array.isArray(orders) ? orders : []
      this.disputes = Array.isArray(disputes) ? disputes : []
      this.messageThreads = messages && Array.isArray(messages.conversations) ? messages.conversations : []
      this.syncOrderVerificationForms(this.orders)
      this.syncManualRefundForms(this.orders)
      this.syncDeliveryArtifactForms(this.orders)
      this.syncDisputeReviewForms(this.disputes)
      const errorPayload = [dashboard, events, sellers, listings, orders, disputes, messages].find((payload) => payload && payload.err)
      if (errorPayload) {
        this.feedback = this.extractError(errorPayload, 'No pudimos cargar Swap.')
        this.feedbackType = 'error'
      }
      this.loading = false
    },
    async loadOptions() {
      const [categories, countries] = await Promise.all([apiService('/api/category/'), apiService('/api/countries/')])
      this.categories = Array.isArray(categories) ? categories : []
      this.countries = Array.isArray(countries) ? countries : []
      const ecuador = this.ecuadorCountries[0]
      if (ecuador && !this.form.country) {
        this.form.country = ecuador.id
        this.loadCities()
      }
    },
    async loadCities() {
      this.form.city = ''
      this.form.venue = ''
      this.venues = []
      if (!this.form.country) return
      const data = await apiService(`/api/cities/?country_id=${encodeURIComponent(this.form.country)}`)
      this.cities = Array.isArray(data) ? data : []
    },
    async loadVenues() {
      this.form.venue = ''
      if (!this.form.city) return
      const data = await apiService(`/api/venues/?city_id=${encodeURIComponent(this.form.city)}`)
      this.venues = Array.isArray(data) ? data : []
    },
    resetForm() {
      this.form = this.defaultForm()
      const ecuador = this.ecuadorCountries[0]
      if (ecuador) this.form.country = ecuador.id
    },
    focusCreate() {
      if (this.$refs.createPanel && this.$refs.createPanel.scrollIntoView) {
        this.$refs.createPanel.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    async createSwapEvent() {
      if (!this.canCreate || this.saving) return
      this.saving = true
      this.feedback = ''
      const payload = {
        name: this.form.name,
        description: this.form.description,
        category: this.form.category || null,
        country: this.form.country || null,
        city: this.form.city || null,
        venue: this.form.venue || null,
        start_date: this.toIso(this.form.start_date),
        end_date: this.toIso(this.form.end_date),
        timezone: this.form.timezone || 'America/Guayaquil',
        default_fee_percent: this.form.default_fee_percent || '0.2000',
        release_after_event_hours: this.form.release_after_event_hours || 24,
      }
      const data = await apiService('/api/admin/swap/events/create/', 'POST', payload)
      this.saving = false
      if (data && data.id && !data.err) {
        this.feedback = `Evento swap creado: ${data.name}`
        this.feedbackType = 'success'
        this.resetForm()
        await this.loadData({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos crear el evento swap.')
      this.feedbackType = 'error'
    },
    async loadOfficialTicketsForEvents(events) {
      const eventList = Array.isArray(events) ? events : []
      await Promise.all(eventList.map((event) => this.loadOfficialTicketsForEvent(event, { silent: true })))
    },
    async loadOfficialTicketsForEvent(event, options = {}) {
      if (!event || !event.id) return
      if (!options.silent) this.loadingOfficialTicketKey = `tickets-${event.id}`
      const data = await apiService(`/api/admin/swap/events/${event.id}/official-tickets/`)
      if (!options.silent) this.loadingOfficialTicketKey = ''
      if (data && !data.err && Array.isArray(data.options)) {
        this.officialTicketPayloads = { ...this.officialTicketPayloads, [event.id]: data }
        return
      }
      if (!options.silent) {
        this.feedback = this.extractError(data, 'No pudimos cargar categorías oficiales.')
        this.feedbackType = 'error'
      }
    },
    async createOfficialTicket(event) {
      if (!this.canCreateOfficialTicket(event) || this.savingOfficialTicketKey) return
      const form = this.officialTicketForm(event)
      this.savingOfficialTicketKey = `tickets-${event.id}`
      this.feedback = ''
      const data = await apiService(`/api/admin/swap/events/${event.id}/official-tickets/`, 'POST', {
        name: String(form.name || '').trim(),
        description: String(form.description || '').trim(),
        price: form.price,
        quantity: Number(form.quantity || 1),
      })
      this.savingOfficialTicketKey = ''
      if (data && !data.err && Array.isArray(data.options)) {
        this.officialTicketPayloads = { ...this.officialTicketPayloads, [event.id]: data }
        this.officialTicketForms = { ...this.officialTicketForms, [event.id]: this.defaultOfficialTicketForm() }
        this.feedback = `Categoría oficial agregada para ${event.name}`
        this.feedbackType = 'success'
        return
      }
      this.feedback = this.extractError(data, 'No pudimos agregar la categoría oficial.')
      this.feedbackType = 'error'
    },
    async reviewSeller(seller, action) {
      if (!seller || !seller.id || this.reviewingKey) return
      this.reviewingKey = `seller-${seller.id}`
      const note = action === 'approve' ? 'Aprobado desde admin Swap.' : action === 'block' ? 'Bloqueado desde admin Swap.' : 'Marcado para revisión desde admin Swap.'
      const data = await apiService(`/api/admin/swap/sellers/${seller.id}/review/`, 'POST', { action, note })
      this.reviewingKey = ''
      if (data && !data.err && data.id) {
        this.feedback = `Seller actualizado: ${data.public_name || data.user_email || data.id}`
        this.feedbackType = 'success'
        await this.loadData({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos actualizar el seller.')
      this.feedbackType = 'error'
    },
    async reviewListing(listing, action) {
      if (!listing || !listing.id || this.reviewingKey) return
      this.reviewingKey = `listing-${listing.id}`
      const note = action === 'approve' ? 'Listing aprobado desde admin Swap.' : action === 'reject' ? 'Listing rechazado desde admin Swap.' : 'Listing marcado para revisión desde admin Swap.'
      const data = await apiService(`/api/admin/swap/listings/${listing.id}/review/`, 'POST', { action, note })
      this.reviewingKey = ''
      if (data && !data.err && data.id) {
        this.feedback = `Listing actualizado: ${data.title || data.event_name || data.id}`
        this.feedbackType = 'success'
        await this.loadData({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos actualizar el listing.')
      this.feedbackType = 'error'
    },
    async verifyOrderPayment(order, status) {
      if (!order || !order.order_number || this.verifyingKey) return
      this.verifyingKey = `order-${order.order_number}`
      const payload = {
        status,
        provider_reference: this.verificationField(order, 'provider_reference'),
        note: this.verificationField(order, 'note'),
      }
      const data = await apiService(`/api/admin/swap/orders/${encodeURIComponent(order.order_number)}/payment-verification/`, 'POST', payload)
      this.verifyingKey = ''
      if (data && !data.err && data.order_number) {
        this.feedback = `Order actualizada: ${data.order_number}`
        this.feedbackType = 'success'
        await this.loadData({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos actualizar la verificación de pago.')
      this.feedbackType = 'error'
    },
    async submitManualRefund(order) {
      if (!this.canManualRefund(order)) return
      this.manualRefundingKey = `refund-${order.order_number}`
      this.feedback = ''
      const form = this.manualRefundForms[order.order_number] || {}
      const payload = new FormData()
      payload.append('amount', form.amount || '')
      payload.append('provider', form.provider || 'paypal')
      payload.append('provider_reference', form.provider_reference || '')
      payload.append('note', form.note || '')
      payload.append('reason', 'manual_refund')
      this.manualRefundAllocationIds(order).forEach((allocationId) => payload.append('allocation_ids', String(allocationId)))
      if (form.proof_file) payload.append('proof_file', form.proof_file)
      const data = await apiServiceForm(`/api/admin/swap/orders/${encodeURIComponent(order.order_number)}/manual-refund/`, 'POST', payload, { timeoutMs: 60000 })
      this.manualRefundingKey = ''
      if (data && !data.err && data.order_number) {
        this.feedback = `Refund manual registrado para ${data.order_number}`
        this.feedbackType = 'success'
        await this.loadData({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos registrar el refund manual.')
      this.feedbackType = 'error'
    },
    async updateDeliveryStatus(order, delivery, status) {
      if (!this.canUpdateDelivery(order, delivery, status)) return
      this.updatingDeliveryKey = `delivery-${delivery.id}-${status}`
      const note = status === 'buyer_accessible' ? 'Delivery habilitado al buyer desde admin Swap.' : 'Delivery movido a progreso desde admin Swap.'
      let data
      if (status === 'buyer_accessible') {
        const form = this.deliveryArtifactForms[delivery.id] || {}
        const payload = new FormData()
        payload.append('status', status)
        payload.append('note', note)
        payload.append('release_to_buyer', 'true')
        payload.append('artifact_title', form.artifact_title || '')
        payload.append('external_reference', form.external_reference || '')
        payload.append('external_url', form.external_url || '')
        payload.append('instructions', form.instructions || '')
        if (form.artifact_file) payload.append('artifact_file', form.artifact_file)
        if (form.artifact_file) payload.append('artifact_type', 'file')
        else if (form.external_url) payload.append('artifact_type', 'external_url')
        else if (form.external_reference) payload.append('artifact_type', 'external_reference')
        else if (form.instructions) payload.append('artifact_type', 'instructions')
        data = await apiServiceForm(`/api/admin/swap/deliveries/${delivery.id}/status/`, 'POST', payload, { timeoutMs: 60000 })
      } else {
        data = await apiService(`/api/admin/swap/deliveries/${delivery.id}/status/`, 'POST', { status, note })
      }
      this.updatingDeliveryKey = ''
      if (data && !data.err && data.order_number) {
        this.feedback = `Delivery actualizado en order ${data.order_number}`
        this.feedbackType = 'success'
        await this.loadData({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos actualizar el delivery.')
      this.feedbackType = 'error'
    },
    messageDraft(threadId) {
      return this.messageDrafts[threadId] || ''
    },
    setMessageDraft(threadId, value) {
      this.messageDrafts = { ...this.messageDrafts, [threadId]: value }
    },
    async sendAdminMessage(thread) {
      if (!thread || !thread.id || this.sendingMessageKey) return
      const body = String(this.messageDraft(thread.id) || '').trim()
      if (!body) return
      this.sendingMessageKey = `thread-${thread.id}`
      const data = await apiService(`/api/admin/swap/messages/${thread.id}/`, 'POST', { body })
      this.sendingMessageKey = ''
      if (data && data.id && !data.err) {
        this.setMessageDraft(thread.id, '')
        this.feedback = 'Mensaje FOMO enviado.'
        this.feedbackType = 'success'
        await this.loadData({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos enviar el mensaje Swap.')
      this.feedbackType = 'error'
    },
    async reviewDispute(dispute, action) {
      if (!dispute || !dispute.id || this.reviewingDisputeKey) return
      this.reviewingDisputeKey = `dispute-${dispute.id}`
      const payload = {
        action,
        resolution: this.disputeReviewField(dispute, 'resolution'),
      }
      const data = await apiService(`/api/admin/swap/disputes/${dispute.id}/review/`, 'POST', payload)
      this.reviewingDisputeKey = ''
      if (data && !data.err && data.id) {
        this.feedback = `Dispute actualizado: ${data.order_number || data.id}`
        this.feedbackType = 'success'
        await this.loadData({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos actualizar el reclamo swap.')
      this.feedbackType = 'error'
    },
    async releaseEligiblePayouts() {
      if (this.releasingPayouts) return
      this.releasingPayouts = true
      this.feedback = ''
      const data = await apiService('/api/admin/swap/payouts/release-eligible/', 'POST', {})
      this.releasingPayouts = false
      if (data && !data.err && Object.prototype.hasOwnProperty.call(data, 'released')) {
        this.feedback = `Payout release evaluado: ${data.released} allocations liberadas, ${data.orders_releasable} orders releasable, ${data.skipped_disputed} bloqueadas por dispute.`
        this.feedbackType = 'success'
        await this.loadData({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos evaluar payouts swap.')
      this.feedbackType = 'error'
    },
    async reservePayoutOrders() {
      if (this.reservingPayouts) return
      this.reservingPayouts = true
      this.feedback = ''
      const data = await apiService('/api/admin/swap/payouts/reserve/', 'POST', {})
      this.reservingPayouts = false
      if (data && !data.err && Object.prototype.hasOwnProperty.call(data, 'payout_orders')) {
        this.feedback = `Payouts reservados: ${data.payout_orders} órdenes, ${data.reserved_allocations} allocations, ${data.skipped_blocked} bloqueadas por recipient.`
        this.feedbackType = 'success'
        await this.loadData({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos reservar payouts swap.')
      this.feedbackType = 'error'
    },
    async syncPayoutOrders() {
      if (this.syncingPayouts) return
      this.syncingPayouts = true
      this.feedback = ''
      const data = await apiService('/api/admin/swap/payouts/sync-orders/', 'POST', {})
      this.syncingPayouts = false
      if (data && !data.err && Object.prototype.hasOwnProperty.call(data, 'paid_allocations')) {
        this.feedback = `Payout sync: ${data.paid_allocations} allocations paid, ${data.released_allocations} reservas liberadas.`
        this.feedbackType = 'success'
        await this.loadData({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos sincronizar payout orders swap.')
      this.feedbackType = 'error'
    },
    openEvent(event) {
      if (!event || !event.id) return
      this.$router.push({ name: 'EventHostView2', params: { slug: event.id }, query: { section: 'editar' } })
    },
    openCheckout(event) {
      if (!event || !event.slug) return
      window.open(`/event/${event.slug}/?preview=1`, '_blank', 'noopener')
    },
  },
}
</script>

<style scoped>
.admin-swap-shell { gap: 20px; }
.admin-swap-hero { overflow: hidden; position: relative; }
.admin-swap-hero::after { content: ''; position: absolute; inset: auto -12% -42% 30%; height: 220px; background: radial-gradient(circle, rgba(98, 255, 214, 0.18), transparent 68%); pointer-events: none; }
.admin-swap-hero__header { position: relative; z-index: 1; }
.admin-swap-hero-actions { display: flex; flex-wrap: wrap; gap: 10px; justify-content: flex-end; }
.admin-swap-metrics { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 14px; position: relative; z-index: 1; }
.admin-swap-grid { display: grid; grid-template-columns: minmax(0, 0.95fr) minmax(340px, 1.05fr); gap: 20px; align-items: start; }
.admin-swap-review-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; align-items: start; }
.admin-swap-form { margin-top: 14px; }
.admin-swap-textarea { resize: vertical; min-height: 112px; }
.admin-swap-policy-strip { display: flex; flex-wrap: wrap; gap: 8px; margin: 16px 0; }
.admin-swap-policy-strip span { border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 999px; padding: 8px 10px; color: var(--text-soft); font-size: 0.82rem; background: rgba(255,255,255,0.05); }
.admin-swap-event-list, .admin-swap-review-list { display: grid; gap: 14px; }
.admin-swap-event-card, .admin-swap-review-card { display: grid; gap: 10px; padding: 16px; border-radius: 20px; border: 1px solid rgba(148, 163, 184, 0.18); background: rgba(255,255,255,0.82); box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08); }
.admin-swap-official-categories { display: grid; gap: 10px; padding: 12px; border-radius: 18px; border: 1px solid rgba(14, 165, 233, 0.16); background: linear-gradient(135deg, rgba(240, 249, 255, 0.86), rgba(236, 253, 245, 0.72)); }
.admin-swap-official-head { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; }
.admin-swap-official-head div { display: grid; gap: 2px; }
.admin-swap-official-head small { color: #0369a1; font-size: 0.66rem; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase; }
.admin-swap-official-head strong { color: var(--text-main); }
.workspace-empty--compact { padding: 10px 12px; border-radius: 14px; font-size: 0.84rem; }
.admin-swap-official-list { display: grid; gap: 8px; }
.admin-swap-official-pill { display: grid; gap: 2px; padding: 10px; border-radius: 14px; background: rgba(255,255,255,0.72); border: 1px solid rgba(14, 165, 233, 0.14); }
.admin-swap-official-pill span { color: #0f766e; font-size: 0.62rem; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase; }
.admin-swap-official-pill strong { color: var(--text-main); }
.admin-swap-official-pill em { color: var(--text-soft); font-size: 0.82rem; font-style: normal; }
.admin-swap-official-form { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(110px, 0.55fr) minmax(96px, 0.45fr) auto; gap: 8px; align-items: flex-end; }
.admin-swap-official-description { grid-column: 1 / -1; }
.admin-swap-order-list, .admin-swap-dispute-list, .admin-swap-message-list { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 14px; }
.admin-swap-order-card { display: grid; gap: 14px; padding: 16px; }
.admin-swap-refund-details { padding: 16px; border-radius: 16px; }
.admin-swap-refund-details summary { cursor: pointer; line-height: 1.5; }
.admin-swap-orders-panel .workspace-actions { gap: 8px; }
.admin-swap-orders-panel .fomo-button { max-width: 100%; }
.swap-ops-filters { display: flex; flex-wrap: wrap; gap: 16px; align-items: end; margin: 16px 0; }
.swap-ops-filters label { display: grid; gap: 8px; }
.swap-ops-filters input, .swap-ops-filters select { background: var(--fomo-control); color: inherit; border: 1px solid var(--fomo-border); border-radius: 12px; padding: 12px; }
.admin-swap-dispute-card { display: grid; gap: 14px; padding: 16px; border-radius: 22px; border: 1px solid rgba(245, 158, 11, 0.22); background: linear-gradient(140deg, rgba(255,255,255,0.94), rgba(255, 247, 237, 0.78)); box-shadow: 0 20px 48px rgba(120, 53, 15, 0.08); }
.admin-swap-message-card { display: grid; gap: 14px; padding: 16px; border-radius: 22px; border: 1px solid rgba(59, 130, 246, 0.2); background: linear-gradient(140deg, rgba(255,255,255,0.94), rgba(239, 246, 255, 0.76)); box-shadow: 0 20px 48px rgba(30, 64, 175, 0.08); }
.admin-swap-order-head { display: flex; justify-content: space-between; gap: 14px; align-items: flex-start; }
.admin-swap-order-total { color: var(--text-main); font-size: 1.2rem; white-space: nowrap; }
.admin-swap-order-meta, .admin-swap-finance-strip { display: flex; flex-wrap: wrap; gap: 8px; color: var(--text-soft); font-size: 0.9rem; }
.admin-swap-order-meta span, .admin-swap-finance-strip span { display: inline-flex; align-items: center; gap: 8px; border: 1px solid rgba(148, 163, 184, 0.18); border-radius: 999px; padding: 7px 10px; background: var(--fomo-control); }
.admin-swap-delivery-review { display: grid; gap: 8px; }
.admin-swap-delivery-row { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 12px; align-items: start; border-radius: 16px; padding: 14px; }
.admin-swap-delivery-row div:first-child { display: grid; gap: 2px; }
.admin-swap-delivery-row strong { color: var(--text-main); }
.admin-swap-delivery-row span { color: var(--text-soft); font-size: 0.86rem; }
.admin-swap-artifact-list { display: grid; gap: 6px; padding-top: 6px; }
.admin-swap-artifact-list article { display: grid; gap: 3px; padding: 9px; border-radius: 13px; background: rgba(15, 118, 110, 0.08); border: 1px solid rgba(20, 184, 166, 0.16); }
.admin-swap-artifact-list small { color: #0f766e; font-size: 0.62rem; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase; }
.admin-swap-artifact-list a { color: #0f766e; font-weight: 900; overflow-wrap: anywhere; }
.admin-swap-delivery-artifact-form { grid-column: 1 / -1; display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; padding-top: 6px; }
.admin-swap-artifact-note { grid-column: 1 / -1; }
.admin-swap-artifact-note textarea { min-height: 74px; resize: vertical; }
.admin-swap-payment-form { display: grid; grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr); gap: 12px; }
.admin-swap-manual-refund-form { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; padding: 16px 0 0; }
.admin-swap-refund-heading { grid-column: 1 / -1; display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; }
.admin-swap-refund-heading div { display: grid; gap: 2px; }
.admin-swap-refund-heading small { color: #b7c1d1; font-size: 0.66rem; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase; }
.admin-swap-refund-heading strong { color: var(--text-main); }
.admin-swap-refund-heading span { color: #b7c1d1; font-size: 0.78rem; font-weight: 800; text-align: right; }
.admin-swap-event-card h4, .admin-swap-review-card h4, .admin-swap-order-card h4, .admin-swap-dispute-card h4, .admin-swap-message-card h4 { margin: 0; color: var(--text-main); font-weight: 900; }
.admin-swap-event-card p, .admin-swap-review-card p, .admin-swap-order-card p, .admin-swap-dispute-card p, .admin-swap-message-card p { margin: 0; color: var(--text-soft); }
.admin-swap-thread-messages { display: grid; gap: 8px; max-height: 280px; overflow: auto; padding-right: 4px; }
.admin-swap-thread-message { display: grid; gap: 4px; padding: 10px 11px; border-radius: 15px; background: rgba(255,255,255,0.68); border: 1px solid rgba(148, 163, 184, 0.16); }
.admin-swap-thread-message small { color: #0f766e; font-size: 0.66rem; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase; }
.admin-swap-thread-message span { color: var(--text-main); line-height: 1.42; }
.admin-swap-thread-message.is-system { border-color: rgba(20, 184, 166, 0.22); }
.admin-swap-thread-message.is-admin { background: rgba(204, 251, 241, 0.62); }
.admin-swap-message-compose { display: grid; gap: 10px; }
.admin-swap-card-meta { display: grid; gap: 6px; color: var(--text-soft); font-size: 0.9rem; }
.admin-swap-card-meta span { display: inline-flex; align-items: center; gap: 8px; }
.admin-swap-proof-strip { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; border-radius: 16px; padding: 12px; background: rgba(236, 253, 245, 0.82); border: 1px solid rgba(20, 184, 166, 0.2); }
.admin-swap-proof-strip div { display: grid; gap: 3px; min-width: 0; }
.admin-swap-proof-strip small { color: #0f766e; font-size: 0.66rem; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase; }
.admin-swap-proof-strip strong { color: var(--text-main); }
.admin-swap-proof-strip span { color: var(--text-soft); font-size: 0.86rem; overflow-wrap: anywhere; }
.admin-swap-proof-strip a { color: #0f766e; font-weight: 900; white-space: nowrap; }
.workspace-button--danger { border-color: rgba(239, 68, 68, 0.36); color: #991b1b; background: rgba(254, 226, 226, 0.74); }
@media (max-width: 1100px) { .admin-swap-grid, .admin-swap-review-grid, .admin-swap-metrics, .admin-swap-payment-form, .admin-swap-delivery-artifact-form, .admin-swap-manual-refund-form, .admin-swap-official-form { grid-template-columns: 1fr; } .admin-swap-delivery-row { grid-template-columns: 1fr; } .admin-swap-proof-strip, .admin-swap-refund-heading, .admin-swap-official-head { flex-direction: column; } .admin-swap-refund-heading span { text-align: left; } }
</style>
